import { useEffect, useState } from 'react';
import { Plus, Trash2, AlertTriangle } from 'lucide-react';
import type { CommissionTierInput } from '@aggregator/shared';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, THead, TBody, TR, TH, TD, EmptyRow } from '@/components/ui/table';
import { PageHeader, Loading, ErrorState } from '@/components/ui/states';
import { useToast } from '@/components/ui/toast';
import {
  useCommissionTiers,
  useCreateTier,
  useUpdateTier,
  useDeleteTier,
  useSettings,
  useUpdateSettings,
} from '@/api/hooks';
import { errorMessage } from '@/api/client';

const emptyTier: CommissionTierInput = {
  minCostUsdt: 0,
  maxCostUsdt: 100,
  fixedUsdt: 1,
  percentage: 10,
};

export function CommissionPage() {
  const data = useCommissionTiers();
  const create = useCreateTier();
  const update = useUpdateTier();
  const del = useDeleteTier();
  const settings = useSettings();
  const updateSettings = useUpdateSettings();
  const { toast } = useToast();
  const [draft, setDraft] = useState<CommissionTierInput>(emptyTier);
  const [webCommission, setWebCommission] = useState('50');

  useEffect(() => {
    if (settings.data) setWebCommission(String(settings.data.publicPriceMarkupPercent ?? 50));
  }, [settings.data]);

  return (
    <div>
      <PageHeader
        title="Commissions"
        description="Configure Telegram selling prices and a separate additional commission for the public web store."
      />

      <Card className="mb-4 border-primary/30">
        <CardHeader>
          <CardTitle>Public web store commission</CardTitle>
        </CardHeader>
        <CardContent>
          {settings.isLoading ? <Loading /> : settings.isError ? (
            <ErrorState error={settings.error} onRetry={() => settings.refetch()} />
          ) : (
            <div className="grid gap-4 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-end">
              <div>
                <LabeledInput
                  label="Web commission (%)"
                  value={Number(webCommission) || 0}
                  onChange={(value) => setWebCommission(String(value))}
                />
                <p className="mt-2 text-xs text-muted-foreground">
                  Added on top of the final Telegram price. For example, a 10 USDT Telegram product with 50% web commission costs 15 USDT on the website.
                </p>
              </div>
              <Button
                loading={updateSettings.isPending}
                onClick={() => updateSettings.mutate(
                  { publicPriceMarkupPercent: Math.max(0, Math.min(1000, Number(webCommission) || 0)) },
                  {
                    onSuccess: () => toast({ title: 'Web commission saved', description: 'New web prices take effect immediately.', variant: 'success' }),
                    onError: (e) => toast({ title: 'Save failed', description: errorMessage(e), variant: 'error' }),
                  },
                )}
              >
                Save web commission
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <h2 className="mb-3 text-lg font-semibold">Telegram bot commission tiers</h2>

      {data.data && data.data.warnings.length > 0 ? (
        <div className="mb-4 space-y-1 rounded-md border border-warning/40 bg-warning/10 p-3 text-sm">
          {data.data.warnings.map((w, i) => (
            <p key={i} className="flex items-center gap-2 text-warning">
              <AlertTriangle className="h-4 w-4" /> {w}
            </p>
          ))}
        </div>
      ) : null}

      <Card className="mb-4">
        <CardHeader>
          <CardTitle>Add tier</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
            <LabeledInput label="Min cost" value={draft.minCostUsdt} onChange={(v) => setDraft({ ...draft, minCostUsdt: v })} />
            <LabeledInput label="Max cost" value={draft.maxCostUsdt} onChange={(v) => setDraft({ ...draft, maxCostUsdt: v })} />
            <LabeledInput
              label="Fixed (USDT)"
              value={draft.fixedUsdt ?? 0}
              onChange={(v) => setDraft({ ...draft, fixedUsdt: v })}
            />
            <LabeledInput
              label="Percentage"
              value={draft.percentage ?? 0}
              onChange={(v) => setDraft({ ...draft, percentage: v })}
            />
            <div className="flex items-end">
              <Button
                className="w-full"
                loading={create.isPending}
                onClick={() =>
                  create.mutate(draft, {
                    onSuccess: () => toast({ title: 'Tier added', variant: 'success' }),
                    onError: (e) => toast({ title: 'Failed', description: errorMessage(e), variant: 'error' }),
                  })
                }
              >
                <Plus className="h-4 w-4" /> Add
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {data.isLoading ? (
            <Loading />
          ) : data.isError ? (
            <ErrorState error={data.error} onRetry={() => data.refetch()} />
          ) : (
            <Table>
              <THead>
                <TR>
                  <TH>Min cost</TH>
                  <TH>Max cost</TH>
                  <TH>Fixed</TH>
                  <TH>Percentage</TH>
                  <TH />
                </TR>
              </THead>
              <TBody>
                {data.data && data.data.tiers.length > 0 ? (
                  data.data.tiers.map((t) => (
                    <TierRow
                      key={t.id}
                      tier={t}
                      onSave={(input) =>
                        update.mutate(
                          { id: t.id, input },
                          {
                            onSuccess: () => toast({ title: 'Tier updated', variant: 'success' }),
                            onError: (e) => toast({ title: 'Failed', description: errorMessage(e), variant: 'error' }),
                          },
                        )
                      }
                      onDelete={() =>
                        del.mutate(t.id, {
                          onSuccess: () => toast({ title: 'Tier deleted', variant: 'success' }),
                        })
                      }
                      saving={update.isPending}
                    />
                  ))
                ) : (
                  <EmptyRow colSpan={5}>No tiers configured — commission will be 0.</EmptyRow>
                )}
              </TBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function LabeledInput({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
}) {
  return (
    <label className="space-y-1 text-xs text-muted-foreground">
      {label}
      <Input type="number" step="0.01" value={value} onChange={(e) => onChange(Number(e.target.value) || 0)} />
    </label>
  );
}

function TierRow({
  tier,
  onSave,
  onDelete,
  saving,
}: {
  tier: { id: string; minCostUsdt: number; maxCostUsdt: number; fixedUsdt: number | null; percentage: number | null };
  onSave: (input: Partial<CommissionTierInput>) => void;
  onDelete: () => void;
  saving: boolean;
}) {
  const [row, setRow] = useState({
    minCostUsdt: tier.minCostUsdt,
    maxCostUsdt: tier.maxCostUsdt,
    fixedUsdt: tier.fixedUsdt ?? 0,
    percentage: tier.percentage ?? 0,
  });
  const dirty =
    row.minCostUsdt !== tier.minCostUsdt ||
    row.maxCostUsdt !== tier.maxCostUsdt ||
    row.fixedUsdt !== (tier.fixedUsdt ?? 0) ||
    row.percentage !== (tier.percentage ?? 0);

  return (
    <TR>
      <TD>
        <Input
          type="number"
          step="0.01"
          value={row.minCostUsdt}
          onChange={(e) => setRow({ ...row, minCostUsdt: Number(e.target.value) || 0 })}
          className="h-8 w-24"
        />
      </TD>
      <TD>
        <Input
          type="number"
          step="0.01"
          value={row.maxCostUsdt}
          onChange={(e) => setRow({ ...row, maxCostUsdt: Number(e.target.value) || 0 })}
          className="h-8 w-24"
        />
      </TD>
      <TD>
        <Input
          type="number"
          step="0.01"
          value={row.fixedUsdt}
          onChange={(e) => setRow({ ...row, fixedUsdt: Number(e.target.value) || 0 })}
          className="h-8 w-24"
        />
      </TD>
      <TD>
        <Input
          type="number"
          step="0.01"
          value={row.percentage}
          onChange={(e) => setRow({ ...row, percentage: Number(e.target.value) || 0 })}
          className="h-8 w-24"
        />
      </TD>
      <TD className="text-right">
        <div className="flex justify-end gap-1">
          {dirty ? (
            <Button size="sm" loading={saving} onClick={() => onSave(row)}>
              Save
            </Button>
          ) : null}
          <Button variant="ghost" size="sm" onClick={onDelete}>
            <Trash2 className="h-4 w-4 text-destructive" />
          </Button>
        </div>
      </TD>
    </TR>
  );
}
